import moment from 'moment-timezone';

export const getBrowserTZ = () => {
  const tz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess();
  // Our CI returns UTC
  return tz === 'UTC' ? 'Australia/Sydney' : tz;
};

export const startOfSupplierDayISOString = ({
  momentDate,
  tz = 'Australia/Sydney',
}: {
  momentDate: moment.Moment | string;
  tz: string;
}) => {
  const base = moment(momentDate)
    .tz(getBrowserTZ())
    // datepicker does not always return midnight
    .startOf('day');
  const converted = base
    .clone()
    // we pass 'true' to preserve the local time (ie. midnight)
    .tz(tz, true)
    .toISOString();
  return converted;
};

export const safeDate =
  (date?: string, timezone?: string) => (format: string) => {
    if (!date || !moment(date).isValid()) {
      return '';
    }
    if (timezone) {
      return moment(date).tz(timezone).format(format);
    }
    return moment(date).format(format);
  };

export const formatDate = (date: moment.Moment | string) => {
  if (!date || !moment(date).isValid()) {
    return '–';
  }
  return moment(date).format('ddd Do MMM YYYY [at] h:mm a');
};

export const momentOrEmpty = (
  raw?: moment.Moment | null | string,
  timezone?: string
) => {
  if (moment(raw).isValid()) {
    return timezone ? moment(raw).tz(timezone) : moment(raw);
  }
  return '';
};

export const dateFormats = {
  dateStamp: 'DD/MM/YY',
  dateTimeStamp: 'DD/MM/YY [at] h:mm a',
  default: 'ddd Do MMM YYYY [at] h:mm a',
};

export const formatDateSupplierTZ = ({
  date,
  supplierTZ = 'Australia/Sydney',
  dateFormat = 'default',
}: {
  date: string | moment.Moment;
  supplierTZ: string;
  dateFormat?: string;
}) => {
  const timezone = supplierTZ || 'Australia/Sydney';

  if (date == null || !moment(date).isValid()) {
    return '–';
  }

  return moment(date).tz(timezone)?.format(dateFormats[dateFormat]);
};

export const momentDateStamp = (
  date?: moment.Moment | null | string,
  timezone?: string,
  dateFormat = 'dateStamp'
) => {
  if (!date) return '';

  if (!moment(date).isValid()) {
    return '';
  }
  return timezone
    ? moment(date).tz(timezone).format(dateFormats[dateFormat])
    : moment(date).format(dateFormats[dateFormat]);
};

export const momentDateTimeStamp = (
  date: moment.Moment | string,
  timezone?: string,
  dateFormat = 'dateTimeStamp'
) => {
  if (!moment(date).isValid()) {
    return '';
  }
  return timezone
    ? moment(date).tz(timezone).format(dateFormats[dateFormat])
    : moment(date).format(dateFormats[dateFormat]);
};

export const formatDateShortFullYear = (
  date: moment.Moment | string,
  timezone?: string
) => {
  if (!date) return '';
  return timezone
    ? moment(date).tz(timezone).format('DD/MM/YYYY')
    : moment(date).format('DD/MM/YYYY');
};

export const formatDateShort = (
  date: moment.Moment | string,
  timezone?: string
) => {
  if (!date) return '';
  return timezone
    ? moment(date).tz(timezone).format('DD/MM/YY')
    : moment(date).format('DD/MM/YY');
};

export const formatDateShortFullMonth = (
  date: moment.Moment | string,
  timezone?: string
) => {
  if (!date) return '';
  return timezone
    ? moment(date).tz(timezone).format('DD MMMM YYYY')
    : moment(date).format('DD MMMM YYYY');
};

export const formatDateNoTime = (
  date: moment.Moment | string,
  timezone?: string
) => {
  if (!date) return '';
  return timezone
    ? moment(date).tz(timezone).format('ddd Do MMM, YYYY')
    : moment(date).format('ddd Do MMM, YYYY');
};

export const formatDateNoTimeFromFormat = (
  date: string,
  format: string,
  timezone?: string
) => {
  if (!date) return '';
  return timezone
    ? moment(date, format).tz(timezone).format('ddd DD MMM, YYYY')
    : moment(date, format).format('ddd DD MMM, YYYY');
};

export const momentTzOrEmpty = (
  raw: moment.Moment | string,
  tz = 'Australia/Sydney'
) => {
  if (moment(raw).isValid()) {
    return moment(raw).tz(tz);
  }
  return '';
};

export const convertToReadableFormat = (value: number) => {
  if (!value || value < 0) return '';

  const days = Math.floor(value / (24 * 60 * 60 * 1000));
  if (days > 0) {
    return days + (days === 1 ? ' day' : ' days');
  }

  const daysms = value % (24 * 60 * 60 * 1000);
  const hours = Math.floor(daysms / (60 * 60 * 1000));
  if (hours > 0) {
    return hours + (hours === 1 ? ' hour' : ' hours');
  }

  const hoursms = value % (60 * 60 * 1000);
  const minutes = Math.floor(hoursms / (60 * 1000));
  if (minutes > 0) {
    return minutes + (minutes === 1 ? ' minute' : ' minutes');
  }

  const minutesms = value % (60 * 1000);
  const sec = Math.floor(minutesms / 1000);

  return sec + (sec === 1 ? ' second' : ' seconds');
};

export const relativeDateFormat = (timestamp: string) => {
  if (!timestamp) return null;

  if (!moment(timestamp).isValid()) {
    return null;
  }

  const formatter = new Intl.RelativeTimeFormat(undefined, {
    numeric: 'auto',
  });

  const DIVISIONS: { amount: number; name: Intl.RelativeTimeFormatUnit }[] = [
    { amount: 60, name: 'seconds' },
    { amount: 60, name: 'minutes' },
    { amount: 24, name: 'hours' },
    { amount: 7, name: 'days' },
    { amount: 4.34524, name: 'weeks' },
    { amount: 12, name: 'months' },
    { amount: Number.POSITIVE_INFINITY, name: 'years' },
  ];

  let duration = (new Date(timestamp).getTime() - new Date().getTime()) / 1000;
  for (const unit of DIVISIONS) {
    if (Math.abs(duration) < unit.amount) {
      return formatter.format(Math.round(duration), unit.name);
    }
    duration /= unit.amount;
  }

  return null;
};

export const getDateDiff = (
  date: string | moment.Moment,
  timezone: string,
  unit: moment.unitOfTime.Diff
) => {
  if (!moment(date).isValid()) {
    return 0;
  }
  const start = moment(date).tz(timezone);
  const end = moment().tz(timezone);

  const diff = end.diff(start, unit);
  return diff;
};

/**
 * Util to get hours of a day in ISO 8601 format (24-hour)
 *
 * use case:
 * can be use to power a modal that let's you pick time in either
 * 12 or 24 hour formats
 *
 * @returns hours
 * [
 *  2024-03-20T01:00:00.000Z,
 *  2024-03-20T02:00:00.000Z,
 *  2024-03-20T03:00:00.000Z,
 *  ...
 *  2024-03-20T22:00:00.000Z
 *  2024-03-20T23:00:00.000Z
 * ]
 */
export const getHoursOfADay = (timezone?: string): string[] => {
  const hours: string[] = [];
  let startOfDay = timezone ? moment().tz(timezone) : moment();
  startOfDay = startOfDay.startOf('day');
  for (let x = 0; x <= 23; x += 1) {
    startOfDay.add(1, 'hour');
    hours.push(startOfDay.toISOString());
  }
  return hours;
};
